import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";

// --- Types matching the .NET API DTOs ---

export interface CommentReply {
  id: string;
  commentId: string;
  userId: string;
  userName: string | null;
  content: string;
  createdAt: string;
}

export interface Comment {
  id: string;
  documentId: string;
  blockId: string | null;
  userId: string;
  userName: string | null;
  content: string;
  resolved: boolean;
  createdAt: string;
  updatedAt: string;
  replies: CommentReply[];
}

export interface CommentCount {
  total: number;
  resolved: number;
  unresolved: number;
}

// --- Query hooks ---

export function useComments(
  docId: string,
  params?: { blockId?: string; resolved?: boolean }
) {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["comments", docId, params],
    queryFn: async () => {
      const searchParams = new URLSearchParams();
      if (params?.blockId) searchParams.set("blockId", params.blockId);
      if (params?.resolved !== undefined)
        searchParams.set("resolved", String(params.resolved));
      const qs = searchParams.toString();
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments${qs ? `?${qs}` : ""}`
      );
      return handleResponse<Comment[]>(response);
    },
    enabled: !!docId,
  });
}

export function useCommentCount(docId: string) {
  const { fetchWithAuth } = useApiContext();

  return useQuery({
    queryKey: ["comment-count", docId],
    queryFn: async () => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments/count`
      );
      return handleResponse<CommentCount>(response);
    },
    enabled: !!docId,
  });
}

// --- Mutation hooks ---

export function useCreateComment(docId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: { blockId?: string; content: string }) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );
      return handleResponse<Comment>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", docId] });
      queryClient.invalidateQueries({ queryKey: ["comment-count", docId] });
    },
  });
}

export function useUpdateComment(docId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async ({
      commentId,
      data,
    }: {
      commentId: string;
      data: { content?: string; resolved?: boolean };
    }) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments/${commentId}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        }
      );
      return handleResponse<Comment>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", docId] });
      queryClient.invalidateQueries({ queryKey: ["comment-count", docId] });
    },
  });
}

export function useDeleteComment(docId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (commentId: string) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments/${commentId}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Failed to delete comment");
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", docId] });
      queryClient.invalidateQueries({ queryKey: ["comment-count", docId] });
    },
  });
}

export function useCreateReply(docId: string, commentId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (data: { content: string }) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments/${commentId}/replies`,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );
      return handleResponse<CommentReply>(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", docId] });
      queryClient.invalidateQueries({ queryKey: ["comment-count", docId] });
    },
  });
}

export function useDeleteReply(docId: string, commentId: string) {
  const queryClient = useQueryClient();
  const { fetchWithAuth } = useApiContext();

  return useMutation({
    mutationFn: async (replyId: string) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${docId}/comments/${commentId}/replies/${replyId}`,
        { method: "DELETE" }
      );
      if (!response.ok) throw new Error("Failed to delete reply");
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["comments", docId] });
      queryClient.invalidateQueries({ queryKey: ["comment-count", docId] });
    },
  });
}
